package com.routexk.quiz.funnel

import kotlinx.browser.window

/**
 * Minimal wrapper over Stripe.js (loaded from https://js.stripe.com/v3/ in the
 * document head). The Payment Element renders card + Apple Pay + Google Pay
 * according to the Stripe dashboard configuration.
 */
object StripeJs {

    fun isLoaded(): Boolean = js("typeof Stripe !== 'undefined'") as Boolean

    fun create(publishableKey: String): dynamic =
        window.asDynamic().Stripe(publishableKey)

    fun createElements(stripe: dynamic, clientSecret: String): dynamic {
        val options = js("{}")
        options.clientSecret = clientSecret
        options.appearance = js("{ theme: 'stripe', variables: { colorPrimary: '#E5322D' } }")
        return stripe.elements(options)
    }

    fun mountPaymentElement(elements: dynamic, containerId: String): dynamic {
        val options = js("{}")
        // Wallets render as dedicated buttons via the Express Checkout Element
        // above the form, so keep them out of the Payment Element tabs.
        options.wallets = js("{ applePay: 'never', googlePay: 'never' }")
        val paymentElement = elements.create("payment", options)
        paymentElement.mount("#$containerId")
        return paymentElement
    }

    /**
     * Mounts the Express Checkout Element: one-click Apple Pay / Google Pay /
     * Link buttons. The wallets appear only where the browser supports them
     * (Apple Pay in Safari, Google Pay in Chrome) and the domain is registered
     * in the Stripe dashboard. [onReady] reports whether any wallet is
     * available so the caller can show/hide the "or pay with card" divider;
     * [onClick] fires when a wallet button is pressed (with its snake_case
     * type, e.g. 'apple_pay'); [onConfirm] must call [confirmPayment].
     */
    fun mountExpressCheckout(
        elements: dynamic,
        containerId: String,
        onReady: (Boolean) -> Unit,
        onClick: (String) -> Unit,
        onConfirm: (String) -> Unit
    ): dynamic {
        val options = js("{ buttonHeight: 48 }")
        val expressElement = elements.create("expressCheckout", options)
        expressElement.mount("#$containerId")
        expressElement.on("ready") { event: dynamic ->
            onReady(event?.availablePaymentMethods != null)
        }
        expressElement.on("click") { event: dynamic ->
            onClick(event?.expressPaymentType as? String ?: "wallet")
            // With a click listener registered, Stripe only opens the payment
            // sheet once resolve() is called
            event.resolve()
        }
        expressElement.on("confirm") { event: dynamic ->
            onConfirm(event?.expressPaymentType as? String ?: "wallet")
        }
        return expressElement
    }

    /**
     * Confirms the payment. Calls back with (errorMessage, transactionId):
     * errorMessage is null on success; transactionId is the PaymentIntent id
     * when the payment resolved in place. With redirect: "if_required" card
     * payments resolve in place; wallets may redirect to [returnUrl].
     */
    fun confirmPayment(
        stripe: dynamic,
        elements: dynamic,
        returnUrl: String,
        onResult: (String?, String?) -> Unit
    ) {
        val params = js("{}")
        params.elements = elements
        params.confirmParams = js("{}")
        params.confirmParams.return_url = returnUrl
        params.redirect = "if_required"
        val promise = stripe.confirmPayment(params)
        promise.then { result: dynamic ->
            val message = result?.error?.message as? String
            onResult(message, result?.paymentIntent?.id as? String)
        }.catch { throwable: dynamic ->
            onResult((throwable?.message as? String) ?: "Payment failed", null)
        }
    }
}
